import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import styles from '../../styles/admin/ImageUploader.module.css';

interface ImageUploaderProps {
  onImagesChange: (urls: string[]) => void;
  existingImages?: string[];
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImagesChange,
  existingImages = [],
}) => {
  const [images, setImages] = useState<string[]>(existingImages);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update images when existingImages prop changes
  useEffect(() => {
    setImages(existingImages);
  }, [existingImages]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setUploading(true);
      setError(null);

      try {
        const newImageUrls = [...images];

        for (const file of acceptedFiles) {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            throw new Error('Only image files are allowed');
          }

          // Validate file size (e.g., 5MB max)
          if (file.size > 5 * 1024 * 1024) {
            throw new Error('File size must be less than 5MB');
          }

          // Generate a unique filename
          const fileExtension = file.name.split('.').pop();
          const fileName = `${uuidv4()}.${fileExtension}`;

          // Create a reference to the file location in Firebase Storage
          const storageRef = ref(storage, `products/${fileName}`);

          // Upload the file
          await uploadBytes(storageRef, file);

          // Get the download URL
          const downloadURL = await getDownloadURL(storageRef);
          newImageUrls.push(downloadURL);
        }

        setImages(newImageUrls);
        onImagesChange(newImageUrls);
        console.log('Uploaded images:', newImageUrls);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to upload images'
        );
        console.error('Upload error:', err);
      } finally {
        setUploading(false);
      }
    },
    [images, onImagesChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    maxFiles: 5,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImagesChange(newImages);
  };

  return (
    <div className={styles.imageUploader}>
      <div
        {...getRootProps()}
        className={`${styles.dropzone} ${isDragActive ? styles.active : ''}`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <p>Uploading...</p>
        ) : (
          <p>Drag & drop images here, or click to select files</p>
        )}
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.imagePreviewContainer}>
        {images.map((url) => (
          <div key={url} className={styles.imagePreview}>
            <img src={url} alt={`Preview ${url}`} />
            <button
              type="button"
              onClick={() => removeImage(images.indexOf(url))}
              className={styles.removeImage}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImageUploader;
